import { useCallback } from "react";

export interface OAuthResult {
  success: boolean;
  email?: string;
  reason?: string;
}

export function useGmailOAuth() {
  const openOAuthPopup = useCallback((authUrl: string, title: string = "gmail-oauth"): Promise<OAuthResult> => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popup = window.open(
      authUrl,
      title,
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );
    
    if (!popup || popup.closed) {
      return Promise.reject(new Error("Popup was blocked. Please allow popups for this site and try again."));
    }

    return new Promise<OAuthResult>((resolve) => {
      let timeoutId: NodeJS.Timeout;
      let pollId: NodeJS.Timeout;
      
      const cleanup = () => {
        window.removeEventListener("message", onMessage);
        clearInterval(pollId);
        clearTimeout(timeoutId);
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== "gmail-oauth-result") return;
        
        cleanup();
        
        if (event.data.success) {
          resolve({ success: true, email: event.data.email });
        } else {
          resolve({ success: false, reason: event.data.reason });
        }
      };
      
      window.addEventListener("message", onMessage);
      
      // Poll to detect if user manually closed the popup
      pollId = setInterval(() => {
        if (popup.closed) {
          cleanup();
          resolve({ success: false, reason: "Popup was closed before completing authorization." });
        }
      }, 500);

      // Max 5 minutes timeout for OAuth process
      timeoutId = setTimeout(() => {
        cleanup();
        resolve({ success: false, reason: "Authorization timed out." });
      }, 5 * 60 * 1000);
    });
  }, []);

  return { openOAuthPopup };
}
